import React from 'react';
import { 
  ShoppingCart, 
  Package, 
  Clock, 
  TrendingUp,
  TrendingDown,
  Activity,
  Users,
  Building,
  Heart,
  AlertTriangle,
  CheckCircle,
  Target,
  BarChart3,
  Calendar,
  Truck,
  DollarSign
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import StatCard from '../../components/StatCard';

const OrdersDashboard: React.FC = () => {
  const { user } = useAuth();

  // Orders KPI Data
  const ordersKPIs = {
    totalOrders: 15847,
    dailyOrders: 89,
    orderGrowth: 8.2,
    fulfillmentRate: 98.5,
    avgProcessingTime: 2.3,
    pendingOrders: 47,
    urgentOrders: 12,
    completedToday: 76
  };

  // Order Volume by Customer Type
  const ordersByCustomerType = [
    { type: 'Aged Care', orders: 7850, percentage: 49.5, avgValue: 285, color: '#8B5CF6' },
    { type: 'NDIS', orders: 3950, percentage: 24.9, avgValue: 195, color: '#10B981' },
    { type: 'Retail', orders: 2890, percentage: 18.2, avgValue: 85, color: '#F59E0B' },
    { type: 'Hospital', orders: 1157, percentage: 7.3, avgValue: 450, color: '#EF4444' }
  ];

  // Daily Order Trends (Last 30 days)
  const dailyOrderTrends = Array.from({ length: 30 }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (29 - i));
    return {
      date: date.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit' }),
      orders: Math.floor(Math.random() * 40) + 60,
      fulfilled: Math.floor(Math.random() * 35) + 55,
      revenue: Math.floor(Math.random() * 15000) + 18000
    };
  });

  // Order Status Distribution
  const orderStatusData = [
    { status: 'Completed', count: 14250, percentage: 89.9, color: '#10B981' },
    { status: 'In Progress', count: 890, percentage: 5.6, color: '#F59E0B' },
    { status: 'Pending Review', count: 485, percentage: 3.1, color: '#8B5CF6' },
    { status: 'On Hold', count: 222, percentage: 1.4, color: '#EF4444' }
  ];

  // Processing Time Analysis
  const processingTimeData = [
    { timeRange: '0-1 hours', orders: 4250, percentage: 26.8 },
    { timeRange: '1-2 hours', orders: 5890, percentage: 37.2 },
    { timeRange: '2-4 hours', orders: 3450, percentage: 21.8 },
    { timeRange: '4-8 hours', orders: 1680, percentage: 10.6 },
    { timeRange: '8+ hours', orders: 577, percentage: 3.6 }
  ];

  // Top Performing Facilities
  const topFacilities = [
    { name: 'Sunnydale Aged Care', orders: 1250, avgTime: 1.8, satisfaction: 4.8, revenue: 356250 },
    { name: 'NDIS Support Services', orders: 890, avgTime: 2.1, satisfaction: 4.6, revenue: 173550 },
    { name: 'Westfield Pharmacy Retail', orders: 2100, avgTime: 0.8, satisfaction: 4.4, revenue: 178500 },
    { name: 'Royal Hospital', orders: 450, avgTime: 3.2, satisfaction: 4.7, revenue: 202500 },
    { name: 'Community Care Centre', orders: 680, avgTime: 2.5, satisfaction: 4.5, revenue: 132600 }
  ];

  // Order Fulfillment Metrics
  const fulfillmentMetrics = [
    { metric: 'Same Day Fulfillment', value: 78.5, target: 75.0, status: 'above' },
    { metric: 'Order Accuracy', value: 99.2, target: 98.5, status: 'above' },
    { metric: 'Customer Satisfaction', value: 4.6, target: 4.5, status: 'above', unit: '/5.0' },
    { metric: 'Average Processing Time', value: 2.3, target: 3.0, status: 'above', unit: ' hours' },
    { metric: 'Rush Order Success Rate', value: 94.8, target: 90.0, status: 'above' },
    { metric: 'Return/Rejection Rate', value: 1.2, target: 2.0, status: 'above' }
  ];

  // Critical Order Alerts
  const criticalOrderAlerts = [
    { type: 'Urgent Orders', count: 12, severity: 'critical', description: 'Orders requiring immediate attention' },
    { type: 'Delayed Orders', count: 8, severity: 'high', description: 'Orders exceeding target processing time' },
    { type: 'Stock Issues', count: 15, severity: 'high', description: 'Orders with stock availability issues' },
    { type: 'Review Required', count: 6, severity: 'medium', description: 'Orders requiring pharmacist review' }
  ];


  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent">
            Orders Dashboard
          </h1>
          <p className="text-gray-600 mt-2">Order fulfillment analytics and performance metrics for {user?.branch}</p>
        </div>
        <div className="text-right">
          <p className="text-sm text-gray-500">Last updated</p>
          <p className="text-sm font-medium text-gray-900">{new Date().toLocaleString()}</p>
        </div>
      </div>

      {/* Orders KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Orders"
          value={ordersKPIs.totalOrders.toLocaleString()}
          subtitle="This year"
          icon={<ShoppingCart className="h-6 w-6" />}
          trend="vs last year"
          trendValue={ordersKPIs.orderGrowth}
          color="text-blue-600"
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Today's Orders"
          value={ordersKPIs.dailyOrders}
          subtitle="Orders processed today"
          icon={<Calendar className="h-6 w-6" />}
          trend="vs yesterday"
          trendValue={5.2}
          color="text-green-600"
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Fulfillment Rate"
          value={`${ordersKPIs.fulfillmentRate}%`}
          subtitle="Orders completed on time"
          icon={<CheckCircle className="h-6 w-6" />}
          trend="vs last month"
          trendValue={1.2}
          color="text-purple-600"
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="Avg Processing Time"
          value={`${ordersKPIs.avgProcessingTime}h`}
          subtitle="From order to fulfillment"
          icon={<Clock className="h-6 w-6" />}
          trend="vs target"
          trendValue={-15.5}
          color="text-orange-600"
          bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
        />
      </div>

      {/* Critical Order Alerts */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-red-600" />
            <div>
              <h3 className="text-xl font-bold text-gray-900">Critical Order Alerts</h3>
              <p className="text-gray-600 text-sm">Orders requiring immediate attention</p>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {criticalOrderAlerts.map((alert, index) => (
            <div key={index} className={`group relative overflow-hidden p-6 rounded-2xl border-2 transition-all duration-300 hover:scale-105 hover:shadow-xl ${
              alert.severity === 'critical' ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-300 hover:border-red-400' :
              alert.severity === 'high' ? 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-300 hover:border-orange-400' :
              'bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-300 hover:border-yellow-400'
            }`}>
              {/* Animated background */}
              <div className={`absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${
                alert.severity === 'critical' ? 'bg-red-500' :
                alert.severity === 'high' ? 'bg-orange-500' :
                'bg-yellow-500'
              }`} />
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-bold text-gray-900 text-lg">{alert.type}</h4>
                  <span className={`px-3 py-1.5 text-sm font-bold rounded-full shadow-lg ${
                    alert.severity === 'critical' ? 'bg-gradient-to-r from-red-500 to-red-600 text-white' :
                    alert.severity === 'high' ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white' :
                    'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white'
                  }`}>
                    {alert.count}
                  </span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{alert.description}</p>
                
                {/* Animated indicator */}
                <div className="mt-4 flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full animate-pulse ${
                    alert.severity === 'critical' ? 'bg-red-500' :
                    alert.severity === 'high' ? 'bg-orange-500' :
                    'bg-yellow-500'
                  }`} />
                  <span className="text-xs font-medium text-gray-600">
                    {alert.severity === 'critical' ? 'Immediate Action Required' :
                     alert.severity === 'high' ? 'High Priority' : 'Medium Priority'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Analytics Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Daily Order Trends */}
        <div className="group bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/30 p-8 hover:shadow-2xl transition-all duration-500 hover:scale-[1.02]">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">Daily Order Trends</h3>
              <p className="text-gray-600 text-sm mt-1">Order volume over the last 30 days</p>
            </div>
            <div className="p-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="h-6 w-6 text-blue-600" />
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={dailyOrderTrends}>
              <defs>
                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: 'none', 
                  borderRadius: '12px', 
                  boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                }} 
                formatter={(value, name) => [
                  name === 'orders' ? `${value} orders` : name === 'fulfilled' ? `${value} fulfilled` : `$${value.toLocaleString()}`,
                  name === 'orders' ? 'Orders' : name === 'fulfilled' ? 'Fulfilled' : 'Revenue'
                ]}
              />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stroke="#8B5CF6" 
                strokeWidth={3}
                fillOpacity={1} 
                fill="url(#colorOrders)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Orders by Customer Type */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Orders by Customer Type</h3>
              <p className="text-gray-600 text-sm">Distribution of order volume</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={ordersByCustomerType}
                cx="50%"
                cy="50%"
                outerRadius={100}
                fill="#8884d8"
                dataKey="orders"
                label={({ type, percentage }) => `${type}: ${percentage}%`}
              >
                {ordersByCustomerType.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => [`${value.toLocaleString()}`, 'Orders']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Order Status Distribution */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Order Status Distribution</h3>
            <p className="text-gray-600 text-sm">Current status of all orders</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {orderStatusData.map((status, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{status.status}</h4>
                <div className="w-4 h-4 rounded-full" style={{ backgroundColor: status.color }}></div>
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Count:</span>
                  <span className="text-sm font-medium text-gray-900">{status.count.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Percentage:</span>
                  <span className="text-sm font-medium text-gray-900">{status.percentage}%</span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      backgroundColor: status.color,
                      width: `${status.percentage}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Fulfillment Performance Metrics */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Fulfillment Performance Metrics</h3>
            <p className="text-gray-600 text-sm">Key performance indicators vs targets</p>
          </div>
          <Target className="h-5 w-5 text-gray-600" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {fulfillmentMetrics.map((metric, index) => (
            <div key={index} className="p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">{metric.metric}</h4>
                {metric.status === 'above' ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
              </div>
              
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current:</span>
                  <span className={`text-sm font-medium ${metric.status === 'above' ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.value}{metric.unit || '%'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Target:</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metric.target}{metric.unit || '%'}
                  </span>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className={`h-2 rounded-full ${metric.status === 'above' ? 'bg-green-500' : 'bg-red-500'}`}
                    style={{ 
                      width: `${Math.min((metric.value / (metric.target * 1.2)) * 100, 100)}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Performing Facilities */}
      <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Top Performing Facilities</h3>
            <p className="text-gray-600 text-sm">Customer performance metrics and satisfaction</p>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Facility</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Orders</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Avg Processing Time</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Satisfaction</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Revenue</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-900">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {topFacilities.map((facility, index) => (
                <tr key={index} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 font-medium text-gray-900">{facility.name}</td>
                  <td className="px-6 py-4 text-gray-900">{facility.orders.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <span className={`font-medium ${facility.avgTime < 2.5 ? 'text-green-600' : 'text-orange-600'}`}>
                      {facility.avgTime}h
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{facility.satisfaction}/5.0</span>
                      <div className="w-16 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${(facility.satisfaction / 5) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900">${facility.revenue.toLocaleString()}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <TrendingUp className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium text-green-600">Excellent</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default OrdersDashboard;
